import { useId, useRef, useState } from "react";

import { Bento } from "@/components/ui/box";
import type { Box, BoxState, FactBox, ListBox, Part, Read, Result, TaskBox } from "@/components/ui/box.schema";
import { frameHook } from "@/components/ui/box.schema";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type { TenantSlug } from "@/lib/theme-presets";

import { INSTRUCTOR, type InstructorFixture, type InstructorState, type Outcome } from "./fixtures";

/**
 * A v2.2's Instructor scenario, drawn with the product's boxes: Prepare,
 * Teach, Close and Review, every scenario a preset of the same presses. The
 * next Close result is chosen beside the page, so every result can be pressed.
 */

type Mark = "Present" | "Late" | "Absent" | "Excused";
const MARKS: Mark[] = ["Present", "Late", "Absent", "Excused"];
const imam = { system: "imam" } as const;
const READ_AT = "Wed, Oct 21, 12:44 AM";
const REMAINS = "Still open: add a recording link once Zoom has one";

interface Publication {
  fields: string[];
  publish: boolean;
}

interface Model {
  marks: Record<string, Mark>;
  recap: string;
  next: string;
  publish: boolean;
  outcome: Outcome;
  closeOpen: boolean;
  conflict: boolean;
  closed: boolean;
  closeResult: Result;
  publication: Publication | null;
  sent: { marks: Record<string, Mark>; publication: Publication } | null;
  rollEditing: boolean;
  rollDraft: Record<string, Mark> | null;
  rollResult: Result;
  handedIn: boolean;
  stale: boolean;
  judgement: "return" | "complete" | null;
  comment: string;
  release: boolean;
  reviewed: Result;
}

const none: Result = { state: "none" };

/** "9 present, 0 absent": present and absent always, late and excused when someone is. */
function count(marks: Record<string, Mark>): string {
  const counts = new Map<Mark, number>();
  for (const mark of Object.values(marks)) counts.set(mark, (counts.get(mark) ?? 0) + 1);
  return MARKS.filter((mark) => mark === "Present" || mark === "Absent" || counts.get(mark)).map((mark) => `${counts.get(mark) ?? 0} ${mark.toLowerCase()}`).join(", ");
}

const names = (fields: string[]) => fields.join(" and ");
const capital = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

function publicationOf(m: Model): Publication {
  const fields = [m.recap.trim() && "recap", m.next.trim() && "next steps"].filter(Boolean) as string[];
  return { fields, publish: m.publish && fields.length > 0 };
}

function publicationLine(p: Publication, learners: number): string {
  if (!p.fields.length) return "Nothing new published: no recap or next steps written";
  return `${capital(names(p.fields))} ${p.publish ? `published to ${learners} learners` : "kept private to the teaching team"}`;
}

function summaryOf(p: Publication | null): string {
  if (!p?.fields.length) return "no recap or next steps written";
  return `${names(p.fields)} ${p.publish ? "published" : "kept private"}`;
}

function base(T: InstructorFixture): Model {
  return {
    marks: {}, recap: T.recap, next: T.next, publish: true, outcome: "confirmed", closeOpen: false, conflict: false, closed: false,
    closeResult: none, publication: null, sent: null, rollEditing: false, rollDraft: null, rollResult: none,
    handedIn: false, stale: false, judgement: null, comment: "", release: true, reviewed: none,
  };
}

/* ---------- the presses, as pure steps on the model ---------- */

function confirmedClose(T: InstructorFixture, m: Model, p: Publication, marks: Record<string, Mark>, cap?: string): Model {
  return {
    ...m, marks: { ...marks }, closed: true, publication: p, conflict: false,
    closeResult: { state: "confirmed", ...(cap ? { cap } : {}), at: READ_AT, effects: [`Roll recorded: ${count(marks)}`, publicationLine(p, T.learners.length)], remains: [REMAINS] },
  };
}

function commitClose(T: InstructorFixture, m: Model, steps: Steps): Model {
  const p = publicationOf(m);
  const marks = { ...m.marks };
  const outcome = m.outcome;
  const next: Model = { ...m, outcome: "confirmed" };
  if (outcome === "refused")
    return { ...next, closeResult: { state: "refused", cap: "Not closed.", sentence: "Your sign-in ended before the close reached IMAM. Sign in again, then close.", draft: "kept" } };
  const sent = { marks, publication: p };
  if (outcome === "unknown")
    return {
      ...next, sent,
      closeResult: {
        state: "unknown", cap: "Close sent", requestId: "specimen-close",
        sentence: `The connection dropped before IMAM answered, so the session may already be held.`,
        check: { verb: "Check Close result", note: "Closing again could repeat it.", run: steps.checkClose },
      },
    };
  if (outcome === "partial" && p.publish)
    return {
      ...next, sent, closed: true, publication: { ...p, publish: false },
      closeResult: {
        state: "partial", cap: "Session held", at: READ_AT,
        went: [{ ids: ["roll"], sentence: `Roll recorded: ${count(marks)}` }],
        failed: [{ ids: p.fields, sentence: `${capital(names(p.fields))}: the server was busy.` }],
        requestId: "specimen-close",
        retry: { verb: "Publish the recap", note: "Sends only the recap and next steps.", run: steps.retryRecap },
      },
    };
  return confirmedClose(T, { ...next, sent }, p, marks);
}

function changedRoll(T: InstructorFixture, m: Model): boolean {
  return Boolean(m.rollDraft && T.learners.some((name) => m.rollDraft![name] !== m.marks[name]));
}

interface Steps {
  checkClose: () => Promise<Result>;
  retryRecap: () => Promise<Result>;
  checkRoll: () => Promise<Result>;
}

/* ---------- the presets: each scenario is a sequence of the same presses ---------- */

interface Start {
  model: Model;
  open: string[];
  intent: string | null;
}

function preset(T: InstructorFixture, state: InstructorState, steps: Steps, outcome: Outcome): Start {
  const start = presetModel(T, state, steps);
  return { ...start, model: { ...start.model, outcome } };
}

function presetModel(T: InstructorFixture, state: InstructorState, steps: Steps): Start {
  const allPresent = (m: Model): Model => ({ ...m, marks: Object.fromEntries(T.learners.map((name) => [name, "Present" as Mark])) });
  const closing = (m: Model): Model => allPresent({ ...m, closeOpen: true });
  const closeWith = (m: Model, outcome: Outcome) => commitClose(T, { ...closing(m), outcome }, steps);
  const m = base(T);
  const reviewOpen = (x: Model): Model => ({ ...x, handedIn: true, judgement: "return" });
  switch (state) {
    case "closing": return { model: closing(m), open: ["close"], intent: "close" };
    case "intent": return { model: { ...reviewOpen(m), comment: T.subject.comment }, open: ["review"], intent: "review" };
    case "intent-blocked": return { model: reviewOpen(m), open: ["review"], intent: "review" };
    case "refused": return { model: closeWith(m, "refused"), open: ["close"], intent: "close" };
    case "unknown": return { model: closeWith(m, "unknown"), open: ["close"], intent: "close" };
    case "partial": return { model: closeWith(m, "partial"), open: ["close"], intent: "close" };
    case "conflict": return { model: { ...closing(m), conflict: true }, open: ["close"], intent: "close" };
    case "closed": return { model: closeWith(m, "confirmed"), open: ["close"], intent: null };
    case "roll": {
      const closed = closeWith(m, "confirmed");
      return { model: { ...closed, rollEditing: true, rollDraft: { ...closed.marks, [T.conflict]: "Late" } }, open: ["close"], intent: "close" };
    }
    case "review": return { model: { ...reviewOpen({ ...closeWith(m, "confirmed"), closeOpen: false }), comment: T.subject.comment }, open: ["review"], intent: null };
    case "stale": return { model: { ...m, stale: true }, open: [], intent: null };
    case "live": return { model: { ...m, handedIn: true }, open: [], intent: null };
    default: return { model: m, open: [], intent: null };
  }
}

/* ---------- small drawn controls ---------- */

function RollRows({ T, marks, onMark, onAll }: { T: InstructorFixture; marks: Record<string, Mark>; onMark: (name: string, mark: Mark) => void; onAll: () => void }) {
  const group = useId();
  return (
    <div className="grid gap-1">
      <p><button type="button" className="text-ui font-semibold text-primary hover:underline" onClick={onAll}>Mark all present</button></p>
      <ul className="m-0 list-none p-0">
        {T.learners.map((name) => (
          <li key={name} className="grid justify-items-start gap-1.5 border-t border-rule py-2 first:border-t-0 sm:flex sm:items-center sm:justify-between">
            <span className="text-body font-semibold">{name}</span>
            {/* One mark per learner: a native radio group, so arrow keys move between marks. */}
            <span role="radiogroup" aria-label={name} className="inline-flex overflow-hidden rounded-control border border-border-strong text-meta">
              {MARKS.map((mark) => (
                <label
                  key={mark}
                  className={cn(
                    "cursor-pointer border-s border-border px-2.5 py-1 text-foreground-soft first:border-s-0 has-[:focus-visible]:outline-2 has-[:focus-visible]:-outline-offset-2 has-[:focus-visible]:outline-ring",
                    marks[name] === mark && (mark === "Absent" ? "bg-status-warning-surface font-semibold text-status-warning-foreground" : "bg-status-success-surface font-semibold text-status-success-foreground"),
                  )}
                >
                  <input type="radio" name={`${group}-${name}`} value={mark} checked={marks[name] === mark} onChange={() => onMark(name, mark)} className="sr-only" />
                  {mark}
                </label>
              ))}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Field({ label, value, onChange, id }: { label: string; value: string; onChange: (value: string) => void; id?: string }) {
  const own = useId();
  return (
    <label className="grid gap-1.5" htmlFor={id ?? own}>
      <span className="text-meta font-semibold text-foreground-soft">{label}</span>
      <Textarea id={id ?? own} rows={3} value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

/* ---------- the page ---------- */

export function InstructorView({ tenant, state, next = "confirmed" }: { tenant: TenantSlug; state: InstructorState; next?: Outcome }) {
  const T = INSTRUCTOR[tenant];
  const N = T.learners.length;
  const S = T.subject;
  const ref = useRef<Model | null>(null);
  const steps: Steps = {
    checkClose: async () => apply((m) => confirmedClose(T, m, m.sent!.publication, m.sent!.marks, "Checked: the first close went through")).closeResult,
    retryRecap: async () => apply((m) => confirmedClose(T, m, publicationOf(m), m.marks)).closeResult,
    checkRoll: async () => apply(confirmRoll).closeResult,
  };
  const [start] = useState(() => preset(T, state, steps, next));
  const [model, setModel] = useState<Model>(start.model);
  ref.current = model;
  const commentId = useId();

  function apply(step: (m: Model) => Model): Model {
    const next = step(ref.current!);
    ref.current = next;
    setModel(next);
    return next;
  }

  function confirmRoll(m: Model): Model {
    const marks = { ...(m.rollDraft ?? m.marks) };
    return {
      ...m, marks, rollEditing: false, rollDraft: null, rollResult: none,
      closeResult: { state: "confirmed", at: READ_AT, effects: [`Roll recorded: ${count(marks)}`, "Recap and next steps unchanged"], remains: [] },
    };
  }

  const m = model;
  const k = T.learners.filter((name) => m.marks[name]).length;
  const p = publicationOf(m);

  /* Prepare: published before class. */
  const prepare: TaskBox<null> = {
    kind: "task", id: "prepare", job: "Publish what learners read before the session", label: "Prepare", owner: imam, read: ready(null), fold: "settled",
    state: () => ({ class: "done", word: "Published" }), detail: () => T.prepare, exits: [{ label: "Show", reopens: true }],
    parts: [{ id: "before", label: "Before the session", state: { class: "done", word: "Published" }, markedByBox: true, owner: imam, fold: "choice", draft: "server", summary: T.before }],
    commit: { verb: "Publish changes", cap: "When you publish", effects: () => [], blockers: () => [], revises: { part: "before", unchanged: () => true }, run: async () => none },
    result: none, draft: false,
  };

  /* Teach: no commit, so a Fact (CASE-023). */
  const teach: FactBox<{ ended: boolean }> = {
    kind: "fact", id: "teach", job: "Teach the live session", label: "Teach", owner: imam, read: ready({ ended: m.closed }), fold: "settled",
    prose: (value) => (value.ended ? "Ended" : "Now"),
    detail: () => T.outline,
    act: m.closed ? undefined : { verb: "Join Zoom meeting", does: "exit", to: "https://zoom.us/", external: true },
  };

  const close = m.rollEditing ? rollTask() : closeTask();

  function rollTask(): TaskBox<null> {
    const unknown = m.rollResult.state === "unknown";
    const changed = changedRoll(T, m);
    const draft = m.rollDraft ?? m.marks;
    const state: BoxState = unknown ? { class: "reading", word: "Roll update not confirmed" } : changed ? { class: "draft", word: "Roll changes, not recorded" } : { class: "done", word: "Held" };
    return {
      kind: "task", id: "close", job: "Close the session", label: "Close", owner: imam, read: ready(null), fold: "settled",
      state: () => state, detail: () => `Recorded: ${count(m.marks)}`,
      act: unknown ? { verb: "Check roll update", does: "check" } : { verb: "Continue roll changes", does: "open" },
      candidate: { eligible: true, blocked: null, rank: 1 },
      parts: [{ id: "roll", label: "Roll", state: changed ? { class: "draft", word: "Not recorded" } : { class: "done", word: "Recorded" }, markedByBox: true, owner: imam, fold: "never", draft: "browser",
        render: () => <RollRows T={T} marks={draft} onMark={(name, mark) => apply((x) => ({ ...x, rollDraft: { ...(x.rollDraft ?? x.marks), [name]: mark } }))} onAll={() => apply((x) => ({ ...x, rollDraft: Object.fromEntries(T.learners.map((name) => [name, "Present" as Mark])) }))} /> }],
      commit: {
        verb: "Save roll changes", cap: "When you update the roll",
        effects: () => [{ id: "roll", sentence: `Record the roll: ${count(draft)}` }, { id: "plan", sentence: "The recap and next steps stay as they are" }],
        blockers: () => [], revises: { part: "roll", unchanged: () => !changed },
        run: async () => {
          const outcome = ref.current!.outcome;
          if (outcome === "refused")
            return apply((x) => ({ ...x, outcome: "confirmed", rollResult: { state: "refused", cap: "Roll not updated.", sentence: "Your sign-in ended before the update reached IMAM. Your changed marks are kept here; the recorded roll is unchanged.", draft: "kept" } })).rollResult;
          if (outcome === "unknown")
            return apply((x) => ({ ...x, outcome: "confirmed", rollResult: { state: "unknown", cap: "Roll update sent, not confirmed", requestId: "specimen-roll", sentence: "The connection dropped before IMAM answered. The roll may already be updated.", check: { verb: "Check roll update", note: "Sending the update again could repeat it.", run: steps.checkRoll } } })).rollResult;
          return apply((x) => confirmRoll({ ...x, outcome: "confirmed" })).closeResult;
        },
      },
      result: m.rollResult, draft: changed,
    };
  }

  function closeTask(): TaskBox<null> {
    const r = m.closeResult;
    const confirmed = r.state === "confirmed";
    let state: BoxState =
      m.closed && confirmed ? { class: "done", word: "Held" }
        : k === N ? { class: "draft", word: "Roll marked, not recorded" }
          : k ? { class: "draft", word: `${k} of ${N} marked` }
            : { class: "waiting", word: "Roll not taken" };
    if (m.conflict) state = { class: "failed", word: "Roll changed since you opened it" };
    if (r.state === "partial") state = { class: "failed", word: "Recap not published" };
    if (r.state === "unknown") state = { class: "reading", word: "Close not confirmed" };

    /* The closed face's line; open, the parts and the foot say it (CASE-025). */
    let detail: string;
    if (m.closed && confirmed) detail = `${count(m.marks)} · ${summaryOf(m.publication)}`;
    else if (r.state === "partial") detail = `Session held · ${count(m.marks)}`;
    else if (r.state === "unknown") detail = `Sent ${READ_AT}`;
    else if (m.conflict) detail = "Your marks are kept";
    else detail = "Recap and next steps not written";

    const recapPart = (failed: boolean): Part => ({
      id: "recap", label: "Recap and next steps", owner: imam, fold: "choice", draft: "browser",
      state: failed ? { class: "draft", word: "Saved" } : p.fields.length ? { class: "draft", word: "Written" } : { class: "quiet", word: "Nothing written" },
      render: () => (
        <>
          <div className="grid gap-5 md:grid-cols-2">
            <Field label="After-session recap" value={m.recap} onChange={(value) => apply((x) => ({ ...x, recap: value }))} />
            <Field label="Next steps" value={m.next} onChange={(value) => apply((x) => ({ ...x, next: value }))} />
          </div>
          {!failed && <p className="text-meta text-muted-foreground">Students still read the earlier recap until you publish.</p>}
        </>
      ),
    });

    const parts: Part[] =
      r.state === "unknown" ? []
        : r.state === "partial" ? [
          { id: "roll", label: "Roll", state: { class: "done", word: "Recorded" }, markedByBox: true, owner: imam, fold: "choice", draft: "none", summary: "Nothing here is sent again." },
          recapPart(true),
        ] : [
          {
            id: "roll", label: "Roll", owner: imam, fold: "never", draft: "browser", markedByBox: true,
            state: m.conflict ? { class: "failed", word: "Changed since you opened it" } : k ? { class: "draft", word: "Not recorded" } : { class: "waiting", word: "Not taken" },
            conflict: m.conflict ? {
              sentence: "Someone recorded the roll at 12:44 AM.",
              facts: [{ label: `Mark for ${T.conflict}`, theirs: "Late" }],
              keepMine: { label: "Keep my roll", run: () => apply((x) => ({ ...x, conflict: false })) },
              loadNewer: { label: "Load newer roll", run: () => apply((x) => ({ ...x, conflict: false, marks: { ...x.marks, [T.conflict]: "Late" } })) },
            } : undefined,
            render: () => <RollRows T={T} marks={m.marks} onMark={(name, mark) => apply((x) => ({ ...x, marks: { ...x.marks, [name]: mark } }))} onAll={() => apply((x) => ({ ...x, marks: Object.fromEntries(T.learners.map((name) => [name, "Present" as Mark])) }))} />,
          },
          recapPart(false),
          { id: "recording", label: "Recording", state: { class: "quiet", word: "No recording" }, owner: { system: "zoom", readAt: null }, fold: "choice", draft: "none", summary: "Add a link after the session." },
        ];

    const faceAct =
      m.closed && confirmed ? undefined
        : r.state === "partial" ? { verb: "Show the recap", does: "open" as const }
          : r.state === "unknown" ? { verb: "Check Close result", does: "check" as const }
            : { verb: k ? "Continue roll" : "Take roll", does: "open" as const };

    return {
      kind: "task", id: "close", job: "Close the session: the roll, the recap and the record that it was held", label: "Close", owner: imam, read: ready(null), fold: "settled",
      state: () => state, detail: () => detail,
            act: faceAct,
      exits: m.closed && confirmed ? [{ label: "Update roll", reopens: true }] : undefined,
      candidate: m.closed && confirmed ? undefined : { eligible: true, blocked: null, rank: 1 },
      parts,
      commit: {
        verb: "Close session", cap: "When you close",
        effects: () => [
          { id: "roll", sentence: `Record the roll: ${count(m.marks)}` },
          { id: "held", sentence: "Mark the session held" },
          ...(p.fields.length ? [{ id: "publish", sentence: `Publish the ${names(p.fields)} to ${N} learners`, optional: { checked: m.publish, toggle: () => apply((x) => ({ ...x, publish: !x.publish })) } }] : []),
        ],
        blockers: () => (k < N ? [{ kind: "missing", part: "roll", sentence: "Mark everyone first." }] : []),
        note: !p.fields.length ? "Nothing written to publish. The published plan stays as it is." : !m.publish ? `Unticked: the ${names(p.fields)} stay private to the teaching team.` : undefined,
        run: async () => apply((x) => commitClose(T, x, steps)).closeResult,
      },
      result: r.state === "partial" && r.retry ? { ...r, retry: { ...r.retry, verb: !p.fields.length ? "Keep changes private" : p.fields.length === 2 ? "Publish the recap" : `Publish the ${names(p.fields)}` } } : r,
      draft: !m.closed && k > 0,
    };
  }

  /* Review: what learners handed in, each row opening its own judgement. */
  type Row = { name: string; subject: boolean; more?: string[] };
  const others = T.learners.filter((name) => name !== S.name);
  const reviewed = m.reviewed.state === "confirmed";
  const reviewRead: Read<{ handedIn: boolean }> = m.stale
    ? { status: "stale", last: { handedIn: false }, readAt: "12:37 AM", sentence: "Couldn't refresh. Later hand-ins aren't shown.", refresh: () => apply((x) => ({ ...x, stale: false, handedIn: true })) }
    : ready({ handedIn: m.handedIn });
  const blockers = (): { kind: "missing"; part: string; sentence: string }[] =>
    !m.judgement ? [{ kind: "missing", part: "judgement", sentence: "Choose a judgement first." }]
      : m.judgement === "return" && !m.comment.trim() ? [{ kind: "missing", part: "comment", sentence: `Write a comment to ${S.short} first.` }] : [];
  const review: ListBox<{ handedIn: boolean }, Row> = {
    kind: "list", id: "review", job: "Judge what learners handed in", label: "Review", owner: imam, read: reviewRead, fold: "choice",
    state: (value) => (!value.handedIn ? { class: "quiet", word: "Nothing handed in" } : reviewed ? { class: "done", word: "Nothing to review" } : { class: "waiting", word: "1 to review" }),
    /* One line says it once; the closed face's second line is for a read time (C4, CASE-025). */
    detail: (value) => (!value.handedIn ? `${T.task}${T.due ? ` · ${T.due}` : ""}` : reviewed ? `${S.short}'s attempt 1 ${m.judgement === "complete" ? "marked complete" : "returned for revision"} · 1 of ${N} handed in` : `1 of ${N} handed in`),
    act: m.handedIn && !reviewed ? { verb: `Review ${S.short}'s attempt`, does: "open" } : undefined,
    exits: reviewed ? [{ label: "Show", reopens: true }] : undefined,
    candidate: m.handedIn && !reviewed ? { eligible: true, blocked: null, rank: 2 } : undefined,
    draft: !reviewed && (m.judgement !== null || m.comment.trim() !== ""),
    rows: (value) => (value.handedIn ? [{ name: S.name, subject: true }, others.length === 1 ? { name: others[0], subject: false } : { name: `${others.length} more learners`, subject: false, more: others }] : []),
    row: {
      key: (row) => row.name,
      title: (row) => row.name,
      // Only the row that differs is marked (K3); the release is the result's to say, never the row's.
      state: (row) => (row.subject ? (reviewed ? { class: "done", word: m.judgement === "complete" ? "Complete" : "Returned" } : { class: "waiting", word: "To review" }) : null),
      facts: (row) => (row.subject ? `Attempt 1 · handed in ${S.at} · ${S.words} words` : row.more ? `${row.more.slice(0, 2).join(", ")} and ${row.more.length - 2} more · no attempt yet` : "No attempt yet"),
      act: (row) => (row.subject ? { verb: "Review", does: "open" } : undefined),
      startsOpen: (row) => row.subject,
      detail: (row) => (row.subject && !reviewed ? <p className="border-s-[3px] border-border-strong ps-3.5 text-foreground">{S.text}</p> : undefined),
      task: (row) => (row.subject ? {
        render: () => reviewed ? null : (
          <div className="grid gap-3">
            <fieldset className="grid gap-1.5">
              <legend className="mb-1 text-meta text-muted-foreground">Your judgement</legend>
              {(["return", "complete"] as const).map((judgement) => (
                <label key={judgement} className="flex items-center gap-2 text-ui">
                  <input type="radio" name={`${commentId}-judgement`} checked={m.judgement === judgement} onChange={() => apply((x) => ({ ...x, judgement }))} className="size-4 accent-primary" />
                  {judgement === "return" ? "Return for revision" : "Complete"}
                </label>
              ))}
            </fieldset>
            <Field id={commentId} label={`Comment to ${S.short}`} value={m.comment} onChange={(value) => apply((x) => ({ ...x, comment: value }))} />
          </div>
        ),
        commit: {
          verb: m.judgement === "complete" ? "Mark complete" : m.judgement === "return" ? "Return for revision" : "Record judgement",
          cap: "When you record your judgement",
          effects: () => [
            { id: "record", sentence: m.judgement === "complete" ? "Record attempt 1 as complete" : "Record attempt 1 as returned for revision" },
            { id: "release", sentence: `Release it to ${S.short} now${m.judgement === "complete" ? "" : ", so a second attempt can be handed in"}`, optional: { checked: m.release, toggle: () => apply((x) => ({ ...x, release: !x.release })) } },
          ],
          blockers,
          run: async () => apply((x) => ({
            ...x,
            reviewed: { state: "confirmed", at: READ_AT, effects: [x.judgement === "complete" ? "Attempt 1 recorded as complete" : "Attempt 1 recorded as returned for revision", x.release ? `Released to ${S.short} with your comment` : `Kept private until you release it to ${S.short}`], remains: [] },
          })).reviewed,
        },
        result: m.reviewed,
      } : undefined),
    },
  };

  const boxes: Box[] = [prepare, teach, close, review];
  return (
    <div className="grid gap-4">
      <header {...frameHook("band")} className="grid gap-0.5">
        <p className="text-meta font-semibold text-muted-foreground">{T.band.eyebrow}</p>
        <h1 className="text-title">{T.band.title}</h1>
        <p className="text-ui text-foreground-soft">{T.band.meta}</p>
      </header>
      <OutcomePicker outcome={m.outcome} rollEditing={m.rollEditing} onPick={(outcome) => apply((x) => ({ ...x, outcome }))} />
      <Bento
        id="instructor"
        journey="instructor-session"
        boxes={boxes}
        defaultOpen={start.open}
        defaultIntent={start.intent}
        onOpenChange={(id, open) => {
          if (id !== "close") return;
          apply((x) => {
            if (open && x.closed && x.closeResult.state === "confirmed" && !x.rollEditing) return { ...x, closeOpen: true, rollEditing: true, rollDraft: { ...x.marks } };
            if (!open && x.rollEditing && !changedRoll(T, x) && x.rollResult.state !== "unknown") return { ...x, closeOpen: false, rollEditing: false, rollDraft: null, rollResult: none };
            return { ...x, closeOpen: open };
          });
        }}
      />
    </div>
  );
}

function ready<T>(value: T): Read<T> {
  return { status: "ready", value, readAt: READ_AT };
}

const OUTCOMES: [Outcome, string][] = [["confirmed", "done"], ["refused", "not done"], ["unknown", "not confirmed"], ["partial", "partly done"]];

/** Specimen control, outside the page: what the next commit returns. */
function OutcomePicker({ outcome, rollEditing, onPick }: { outcome: Outcome; rollEditing: boolean; onPick: (outcome: Outcome) => void }) {
  return (
    <div role="group" aria-label="What the next press returns" data-specimen-control="outcome" className="flex flex-wrap items-center gap-2 text-meta text-muted-foreground">
      <span>What the next {rollEditing ? "roll update" : "close"} returns:</span>
      {OUTCOMES.filter(([value]) => !rollEditing || value !== "partial").map(([value, label]) => (
        <button key={value} type="button" aria-pressed={outcome === value} data-outcome={value} onClick={() => onPick(value)} className={cn("rounded-control border border-border px-2 py-0.5", outcome === value && "border-primary font-semibold text-foreground")}>
          {label}
        </button>
      ))}
    </div>
  );
}
